import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import InstitutionQuickActions from '../../components/ui/InstitutionQuickActions';
import InstitutionHeader from './components/InstitutionHeader';
import ProgramCard from './components/ProgramCard';
import AdmissionRequirements from './components/AdmissionRequirements';
import CampusFacilities from './components/CampusFacilities';
import StudentTestimonials from './components/StudentTestimonials';
import AccreditationSection from './components/AccreditationSection';
import Icon from '../../components/AppIcon';

const InstitutionProfile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('programs');

  const institutionData = {
    name: "Beijing Language and Culture University",
    location: "Beijing, China",
    established: 1962,
    ranking: "Top 10 Language Universities",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_10089777c-1768718566633.png",
    logoAlt: "Beijing Language and Culture University official logo with red and gold Chinese characters on white background",
    coverImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb79556e-1767072446865.png",
    coverImageAlt: "Modern university campus building with traditional Chinese architectural elements, red pillars and students walking on sunny day",
    badges: [
    { icon: "Award", label: "MOE Certified" },
    { icon: "Globe", label: "International Programs" },
    { icon: "Users", label: "5000+ Students" }],

    stats: [
    { icon: "Users", value: "5,000+", label: "International Students" },
    { icon: "BookOpen", value: "50+", label: "Language Programs" },
    { icon: "GraduationCap", value: "95%", label: "Success Rate" },
    { icon: "Globe", label: "Partner Universities", value: "120+" }]

  };

  const programs = [
  {
    id: 1,
    name: "Intensive Chinese Language Program",
    description: "Comprehensive Mandarin Chinese course designed for beginners to advanced learners with focus on speaking, listening, reading and writing skills through immersive teaching methods",
    duration: "6 months",
    classSize: "15-20",
    tuition: "¥18,000/semester",
    featured: true,
    features: ["HSK Preparation", "Cultural Activities", "Language Exchange", "Certificate"]
  },
  {
    id: 2,
    name: "Business Chinese Program",
    description: "Specialized program focusing on business communication, professional vocabulary and Chinese business culture for working professionals and business students",
    duration: "1 year",
    classSize: "12-15",
    tuition: "¥22,000/semester",
    featured: false,
    features: ["Business Focus", "Internship Support", "Networking Events", "Certificate"]
  },
  {
    id: 3,
    name: "Chinese Culture & Language",
    description: "Integrated program combining language learning with deep cultural immersion through traditional arts, calligraphy, tea ceremony and Chinese philosophy studies",
    duration: "1 semester",
    classSize: "20-25",
    tuition: "¥16,000/semester",
    featured: false,
    features: ["Cultural Immersion", "Art Classes", "Field Trips", "Certificate"]
  }];


  const requirements = {
    academic: [
    "High school diploma or equivalent qualification from recognized institution",
    "Minimum age of 18 years at time of application submission",
    "Good academic standing with minimum GPA of 2.5 or equivalent",
    "No previous disciplinary actions or academic misconduct records"],

    language: [
    { test: "IELTS", score: "5.5+" },
    { test: "TOEFL", score: "70+" },
    { test: "HSK", score: "Level 3+" },
    { test: "Duolingo", score: "95+" }],

    documents: [
    { name: "Valid Passport", format: "PDF, clear scan" },
    { name: "Academic Transcripts", format: "PDF, certified" },
    { name: "Passport Photo", format: "JPG, white background" },
    { name: "Health Certificate", format: "PDF, recent" },
    { name: "Financial Statement", format: "PDF, bank certified" },
    { name: "Recommendation Letter", format: "PDF, signed" }],

    timeline: [
    { date: "Jan 15, 2026", event: "Application Opens" },
    { date: "Mar 31, 2026", event: "Application Deadline" },
    { date: "Apr 30, 2026", event: "Admission Results" },
    { date: "Jun 15, 2026", event: "Visa Processing" },
    { date: "Sep 01, 2026", event: "Semester Begins" }]

  };

  const facilities = {
    highlights: [
    {
      image: "https://images.unsplash.com/photo-1701282557983-a5e42df83f4d",
      imageAlt: "Modern university library interior with rows of wooden study desks, floor-to-ceiling bookshelves and students studying under warm lighting",
      icon: "BookOpen",
      name: "Modern Library",
      description: "State-of-the-art library with over 500,000 Chinese and international books, digital resources and quiet study spaces"
    },
    {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_19e809c32-1767114491226.png",
      imageAlt: "University gymnasium with basketball court, modern sports equipment and high ceiling with natural lighting from large windows",
      icon: "Dumbbell",
      name: "Sports Complex",
      description: "Comprehensive sports facilities including gymnasium, swimming pool, tennis courts and fitness center for student wellness"
    },
    {
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ebda010b-1765430406464.png",
      imageAlt: "Modern student dormitory room with twin beds, study desks, large windows and contemporary furniture in neutral colors",
      icon: "Home",
      name: "Student Housing",
      description: "Comfortable on-campus dormitories with modern amenities, WiFi, laundry facilities and 24/7 security for international students"
    }],

    amenities: [
    { icon: "Wifi", name: "High-Speed WiFi" },
    { icon: "Utensils", name: "Cafeteria" },
    { icon: "Bus", name: "Shuttle Service" },
    { icon: "ShoppingBag", name: "Campus Store" },
    { icon: "Coffee", name: "Coffee Shops" },
    { icon: "Heart", name: "Health Center" }],

    location: {
      name: "Beijing Language and Culture University",
      lat: 39.9612,
      lng: 116.3538,
      nearby: [
      "15 min to Subway Station",
      "20 min to City Center",
      "30 min to Beijing Airport",
      "Walking distance to Shopping Mall"]

    }
  };

  const testimonials = [
  {
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d92ac120-1763293804988.png",
    avatarAlt: "Professional headshot of young Asian woman with long black hair wearing white blouse smiling warmly at camera",
    name: "Sarah Johnson",
    program: "Intensive Chinese",
    year: "2025",
    rating: 5,
    country: "United States",
    content: "The program exceeded my expectations. The teachers are incredibly patient and the immersive environment helped me achieve HSK 5 in just one year. The cultural activities and language exchange programs made learning enjoyable and practical."
  },
  {
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a8edcce5-1763294258292.png",
    avatarAlt: "Professional headshot of young Caucasian man with short brown hair wearing navy blue suit and tie smiling confidently",
    name: "Marco Silva",
    program: "Business Chinese",
    year: "2024",
    rating: 5,
    country: "Brazil",
    content: "Perfect for professionals! The business-focused curriculum and networking opportunities opened doors for my career in China. The internship support helped me secure a position at a leading Chinese company. Highly recommended for business students."
  },
  {
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5b689fb-1763296744210.png",
    avatarAlt: "Professional headshot of young African woman with natural curly hair wearing red blazer smiling brightly at camera",
    name: "Amara Okafor",
    program: "Culture & Language",
    year: "2025",
    rating: 5,
    country: "Nigeria",
    content: "An unforgettable experience! Learning Chinese while exploring the rich culture through calligraphy, tea ceremony, and traditional arts made every day exciting. The teachers genuinely care about student success and the campus facilities are excellent."
  },
  {
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab4a8204-1763295598308.png",
    avatarAlt: "Professional headshot of young Middle Eastern man with black beard wearing gray suit smiling warmly at camera",
    name: "Ahmed Hassan",
    program: "Intensive Chinese",
    year: "2024",
    rating: 5,
    country: "Egypt",
    content: "The structured curriculum and supportive environment made learning Chinese achievable. Small class sizes ensure personalized attention. The location in Beijing provides authentic language practice opportunities daily. Great value for money and life-changing experience."
  }];


  const accreditations = {
    logos: [
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1bf139b22-1767940455245.png",
      logoAlt: "Ministry of Education China official seal with red and gold colors featuring Chinese characters and national emblem",
      name: "MOE China"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12c3236d3-1764657838848.png",
      logoAlt: "ISO 9001 quality certification badge with blue and white design showing international standards compliance",
      name: "ISO 9001"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_135b1ecf4-1768718567272.png",
      logoAlt: "AACSB accreditation logo with blue shield design representing business education excellence standards",
      name: "AACSB"
    },
    {
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1da0fef80-1768718568238.png",
      logoAlt: "Confucius Institute headquarters logo with red circular design and Chinese calligraphy representing cultural education",
      name: "Confucius Institute"
    }],

    recognitions: [
    {
      title: "Ministry of Education Approved",
      description: "Officially recognized by Chinese Ministry of Education for international student enrollment"
    },
    {
      title: "HSK Test Center",
      description: "Authorized HSK testing center for all levels of Chinese proficiency examinations"
    },
    {
      title: "Global University Network",
      description: "Partnership with 120+ universities worldwide for student exchange programs"
    },
    {
      title: "Quality Assurance Certified",
      description: "ISO 9001 certified for educational quality management and student services"
    }]

  };

  const tabs = [
  { id: 'programs', label: 'Programs', icon: 'BookOpen' },
  { id: 'admission', label: 'Admission', icon: 'FileCheck' },
  { id: 'facilities', label: 'Facilities', icon: 'Building2' },
  { id: 'testimonials', label: 'Testimonials', icon: 'MessageSquare' },
  { id: 'accreditation', label: 'Accreditation', icon: 'Award' }];


  const handleApply = (programId) => {
    navigate('/application-form', { state: { programId } });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20">
        <Breadcrumbs />
        
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="mb-6 md:mb-8">
            <InstitutionHeader institution={institutionData} />
          </div>

          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="flex-1 min-w-0">
              <div className="bg-card rounded-lg shadow-warm-sm border border-border mb-6 md:mb-8 overflow-x-auto">
                <div className="flex border-b border-border min-w-max">
                  {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                        flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 font-caption font-medium transition-smooth flex-shrink-0
                        ${activeTab === tab?.id ?
                    'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}
                      `
                    }>

                      <Icon name={tab?.icon} size={18} />
                      <span className="text-sm md:text-base">{tab?.label}</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-6 md:space-y-8">
                {activeTab === 'programs' &&
                <div className="space-y-4 md:space-y-5 lg:space-y-6">
                    <div className="flex items-center justify-between mb-4 md:mb-6">
                      <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground">
                        Available Programs
                      </h2>
                      <span className="text-sm md:text-base text-muted-foreground font-caption">
                        {programs?.length} programs
                      </span>
                    </div>
                    {programs?.map((program) =>
                  <ProgramCard
                    key={program?.id}
                    program={program}
                    onApply={handleApply} />

                  )}
                  </div>
                }

                {activeTab === 'admission' &&
                <div>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
                      Admission Requirements
                    </h2>
                    <AdmissionRequirements requirements={requirements} />
                  </div>
                }

                {activeTab === 'facilities' &&
                <div>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
                      Campus Facilities
                    </h2>
                    <CampusFacilities facilities={facilities} />
                  </div>
                }

                {activeTab === 'testimonials' &&
                <div>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
                      Student Testimonials
                    </h2>
                    <StudentTestimonials testimonials={testimonials} />
                  </div>
                }

                {activeTab === 'accreditation' &&
                <div>
                    <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4 md:mb-6">
                      Accreditation & Recognition
                    </h2>
                    <AccreditationSection accreditations={accreditations} />
                  </div>
                }
              </div>
            </div>

            <div className="lg:w-80 xl:w-96 flex-shrink-0">
              <InstitutionQuickActions
                institutionId={institutionData?.name}
                institutionName={institutionData?.name} />

            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default InstitutionProfile;